//Proofed
import { useState, useEffect } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip, CircularProgress, IconButton } from '@mui/material'
import { FancyGoldButton } from '../components/FancyGoldButton'
import { FancyLightButton } from '../components/FancyLightButton'
import { BookDemoModal } from '../components/BookDemoModal'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import StarIcon from '@mui/icons-material/Star'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import { BORDER_BLACK, COLOR_PRIMARY, COLOR_QUATERNARY, COLOR_SECONDARY, COLOR_TERTIARY, HOME_HERO_GRADIENT, HOME_HERO_PANEL_BG, DARK_800, DARK_900, BG_LIGHT, CHECKCIRCLE_SIZE, BORDER_WHITE } from '../theme'
import { Link } from 'react-router-dom'
import { buildApiUrl, CONTRACT_NUM } from '../common/common'
import PageMeta from '../components/PageMeta'
import { freetrialpoints, homeSteps, homeStepsTitle, productCards, stats } from '../common/HomeContent'
import { TrustedPartnerSection } from '../components/TrustedPartnerSection'
import { VideoModal } from '../components/VideoModal'
import { HowItWorksSection } from '../components/HowItWorksSection'
import { StatsSection } from '../components/StatsSection'
import { CtaSection } from '../components/CtaSection'
import { WaveDivider } from '../components/WaveDivider'
import { ProblemBox } from '../components/ProblemBox'
import { testimonials } from '../common/Testimonials'
import { homeProblems, homeProblemStatement } from '../common/ProblemContent'

interface LiveTender {
    publisher: string
    reftitleshort: string
    valuemin: number | null
    valuemax: number | null
    cpvprimaryhtml: string
    closingdate: string
}

function fmtValue(min: number | null, max: number | null): string {
    const v = max ?? min
    if (!v) return 'TBC'
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
    if (v >= 1_000) return `${Math.round(v / 1_000)}K`
    return `${v.toLocaleString()}`
}

function fmtCategory(cpv: string): string {
    const idx = cpv.indexOf(':')
    return idx >= 0 ? cpv.slice(idx + 1) : cpv
}

function fmtDeadline(closingDate: string): string {
    const diff = new Date(closingDate).getTime() - Date.now()
    if (diff <= 0) return 'Closed'
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24))
    if (days === 1) return '1 day'
    if (days < 7) return `${days} days`
    const weeks = Math.round(days / 7)
    if (weeks === 1) return '1 week'
    if (weeks < 52) return `${weeks} weeks`
    const years = Math.round(weeks / 52)
    return `${years} ${years === 1 ? 'year' : 'years'}`
}

export default function Home() {
    const [liveTenders, setLiveTenders] = useState<LiveTender[]>([])
    const [liveTotal, setLiveTotal] = useState<number | null>(null)
    const [loadingTenders, setLoadingTenders] = useState(true)
    const [activeTab, setActiveTab] = useState(0)
    const [carouselIdx, setCarouselIdx] = useState(0)
    const [demoOpen, setDemoOpen] = useState(false)
    const [videoOpen, setVideoOpen] = useState(false)
    const [videoId, setVideoId] = useState('')
    const [videoAspect, setVideoAspect] = useState('56.25%')

    useEffect(() => {
        fetch(buildApiUrl())
            .then(res => res.json())
            .then(data => {
                setLiveTenders(data.results ?? [])
                setLiveTotal(data.total ?? null)
            })
            .catch(() => {/* silent fail panel stays empty */ })
            .finally(() => setLoadingTenders(false))
    }, [])

    return (
        <Box>
            <PageMeta
                title="Contract Finder Pro | UK & Ireland Tender Advisory & Research Service"
                description="Contract Finder Pro is the UK & Ireland tender advisory and research service. Discover public sector contract opportunities with real-time alerts, AI bid writing and market analytics."
                canonical="/"
            />
            {/* Hero */}
            <Box sx={{
                background: HOME_HERO_GRADIENT,
                pt: { xs: 2, md: 6 },
                pb: { xs: 3, md: 10 },
                overflow: 'visible',
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
                        <Grid item xs={12} md={6}>

                            <Box sx={{ mb: 3, lineHeight: 1.15 }}>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        color: 'rgba(255,255,255,0.55)',
                                        fontSize: { xs: '22px', md: '26px', lg: '36px' },
                                        fontWeight: 500,
                                        lineHeight: 1.3,
                                        mb: 0.5,
                                    }}
                                >
                                    Find the right tenders
                                </Typography>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        color: 'rgba(255,255,255,0.85)',
                                        fontSize: { xs: '30px', md: '38px', lg: '44px' },
                                        fontWeight: 600,
                                        lineHeight: 1.2,
                                        mb: 0.5,
                                    }}
                                >
                                    Write stronger bids
                                </Typography>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        color: COLOR_PRIMARY,
                                        fontSize: { xs: '38px', md: '50px', lg: '58px' },
                                        fontWeight: 700,
                                        lineHeight: 1.1,
                                    }}
                                >
                                    Win more public sector work
                                </Typography>
                            </Box>
                            <Typography
                                variant="body1"
                                sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, fontSize: '18px', lineHeight: 1.7, maxWidth: 480 }}
                            >
                                One platform combining complete tender coverage, buyer intelligence and safe AI bid writing. Built for UK & Ireland public sector suppliers
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                                <FancyGoldButton to="/register" endIcon={<ArrowForwardIcon />}>
                                    Join free for 14 days
                                </FancyGoldButton>
                                <FancyLightButton onClick={() => setDemoOpen(true)}>Book Demo</FancyLightButton>
                            </Stack>
                            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                                {freetrialpoints.map(text => (
                                    <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <CheckCircleIcon sx={{ fontSize: CHECKCIRCLE_SIZE, color: COLOR_TERTIARY }} />
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>{text}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>

                        {/* Live Tenders panel */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }} >
                                <Chip
                                    label="UK & Ireland Tender Advisory & Research Service"
                                    size="medium"
                                    sx={{
                                        mb: 1,
                                        backgroundColor: 'rgba(244,182,50,0.15)',
                                        color: COLOR_PRIMARY,
                                        border: '1px solid rgba(244,182,50,0.3)',
                                        fontWeight: 600,
                                        fontSize: '12px',
                                        letterSpacing: '0.3px',
                                    }}
                                />
                            </Box>
                            <Box component={Link} to="/tenderresults" sx={{
                                background: HOME_HERO_PANEL_BG,
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 3,
                                overflow: 'hidden',
                                backdropFilter: 'blur(12px)',
                                display: 'block',
                                textDecoration: 'none',
                                cursor: 'pointer',
                                transition: 'border-color 0.2s',
                                '&:hover': { borderColor: 'rgba(255,255,255,0.25)' },
                            }}>

                                <Box sx={{
                                    px: 2.5, py: 1,
                                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                }}>

                                    <Box sx={{
                                        width: 8, height: 8, borderRadius: '50%',
                                        backgroundColor: COLOR_TERTIARY,
                                        boxShadow: `0 0 6px ${COLOR_TERTIARY}`,
                                    }} />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: '14px' }}>
                                        Live Tender Alerts
                                    </Typography>
                                    {!loadingTenders && liveTotal != null && (
                                        <Chip
                                            label={`${liveTotal.toLocaleString()} live`}
                                            size="small"
                                            sx={{ ml: 'auto', backgroundColor: 'rgba(244,182,50,0.15)', color: COLOR_PRIMARY, fontSize: '11px', fontWeight: 600, height: 22 }}
                                        />
                                    )}
                                </Box>
                                {loadingTenders ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress size={24} sx={{ color: COLOR_PRIMARY }} />
                                    </Box>
                                ) : (
                                    <Stack divider={<Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }} />}>
                                        {liveTenders.slice(0, 4).map((tender, i) => (
                                            <Box key={i} sx={{ px: 2.5, py: 1.5 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                                    <Typography sx={{ color: COLOR_PRIMARY, fontWeight: 600, fontSize: '13px' }}>
                                                        {tender.publisher}
                                                    </Typography>
                                                    <Chip
                                                        label={fmtCategory(tender.cpvprimaryhtml)}
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '11px', backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', maxWidth: 120 }}
                                                    />
                                                </Box>
                                                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', mb: 1, lineHeight: 1.4 }}>
                                                    {tender.reftitleshort}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                                                        Value: <Box component="span" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>{fmtValue(tender.valuemin, tender.valuemax)}</Box>
                                                    </Typography>
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                                                        Deadline: <Box component="span" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>{fmtDeadline(tender.closingdate)}</Box>
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                                <Box sx={{
                                    px: 2.5, py: 1.5,
                                    borderTop: '1px solid rgba(255,255,255,0.06)',
                                    display: 'flex', justifyContent: 'center',
                                }}>
                                    <FancyLightButton to="/tenderresults" transparent noBorder textColor={COLOR_PRIMARY} endIcon={<ArrowForwardIcon />}>
                                        View all live tenders
                                    </FancyLightButton>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* Products section */}
            <Box sx={{
                // border: BORDER_BLACK,
                backgroundColor: BG_LIGHT, py: { xs: 2, md: 4 }
            }}>
                <Container maxWidth="lg">
                    {/* Tab bar */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 2,
                        gap: { xs: 1, md: 1 },
                        flexWrap: 'wrap',
                    }}>
                        {productCards.map((product, i) => {
                            const selected = activeTab === i
                            return (
                                <Box key={product.name} sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1, md: 1 },
                                }}>
                                    <Box
                                        onClick={() => setActiveTab(i)}
                                        onMouseEnter={() => setActiveTab(i)}
                                        sx={{
                                            py: 1.5,
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
                                            width: { xs: 130, md: 180 },
                                            height: { xs: 110, md: 128 },
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            backgroundColor: selected ? 'white' : 'transparent',
                                            border: selected ? `3px solid ${product.color}70` : '3px solid rgba(0,0,0,0.1)',
                                            boxShadow: selected ? `0 2px 12px ${product.color}20` : 'none',
                                            transition: 'all 0.2s',
                                            userSelect: 'none',
                                            flexShrink: 0,
                                            '&:hover': !selected ? { backgroundColor: 'rgba(255,255,255,0.7)', borderColor: `${product.color}50` } : {},
                                        }}
                                    >
                                        <Box sx={{
                                            width: 36, height: 36, borderRadius: '50%',
                                            backgroundColor: selected ? `${product.color}18` : 'rgba(0,0,0,0.05)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            mb: 0.5,
                                            border: selected ? `1.5px solid ${product.color}40` : '1.5px solid transparent',
                                        }}>
                                            <product.Icon sx={{ fontSize: 18, color: selected ? product.color : 'text.disabled' }} />
                                        </Box>
                                        <Typography sx={{
                                            fontWeight: 700,
                                            color: selected ? product.color : 'text.secondary',
                                            fontSize: { xs: '13px', md: '14px' },
                                            textAlign: 'center',
                                            lineHeight: 1.3,
                                            mb: 0.5,
                                        }}>
                                            {product.tabLabel}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: '12px',
                                            color: selected ? 'text.secondary' : 'rgba(0,0,0,0.35)',
                                            textAlign: 'center',
                                            lineHeight: 1.3,
                                            maxWidth: 130,
                                        }}>
                                            {product.tabSubtitle}
                                        </Typography>
                                    </Box>

                                    {/* Arrow connector between steps, "=" before Win */}
                                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', color: 'rgba(0,0,0,0.3)' }}>
                                        {i < productCards.length - 1
                                            ? <ArrowForwardIcon sx={{ fontSize: { sm: 24, md: 32 } }} />
                                            : <Typography sx={{ fontSize: { sm: '24px', md: '32px' }, fontWeight: 700, lineHeight: 1 }}>=</Typography>
                                        }
                                    </Box>
                                </Box>
                            )
                        })}

                        {/* Win badge */}
                        <Box
                            onClick={() => setActiveTab(3)}
                            onMouseEnter={() => setActiveTab(3)}
                            sx={{
                                py: 1.25,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
                                width: { xs: 130, md: 180 },
                                height: { xs: 110, md: 128 },
                                borderRadius: 2,
                                cursor: 'pointer',
                                backgroundColor: activeTab === 3 ? 'white' : 'transparent',
                                border: activeTab === 3 ? '3px solid rgba(70,145,39,0.6)' : '3px solid rgba(0,0,0,0.1)',
                                boxShadow: activeTab === 3 ? '0 2px 12px rgba(70,145,39,0.2)' : 'none',
                                transition: 'all 0.2s',
                                userSelect: 'none',
                                flexShrink: 0,
                                '&:hover': activeTab !== 3 ? { backgroundColor: 'rgba(255,255,255,0.7)', borderColor: 'rgba(70,145,39,0.5)' } : {},
                            }}
                        >
                            <Box sx={{
                                width: 36, height: 36, borderRadius: '50%',
                                backgroundColor: activeTab === 3 ? 'rgba(70,145,39,0.18)' : 'rgba(0,0,0,0.05)',
                                border: activeTab === 3 ? '1.5px solid rgba(70,145,39,0.4)' : '1.5px solid transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                mb: 0.5
                            }}>
                                <WorkspacePremiumIcon sx={{ fontSize: 18, color: activeTab === 3 ? COLOR_TERTIARY : 'text.disabled' }} />
                            </Box>
                            <Typography sx={{
                                fontWeight: 700,
                                color: activeTab === 3 ? COLOR_TERTIARY : 'text.secondary',
                                fontSize: { xs: '13px', md: '14px' },
                                textAlign: 'center',
                                mb: 0,
                            }}>
                                Win
                            </Typography>
                            <Typography sx={{
                                fontSize: '12px',
                                color: activeTab === 3 ? 'text.secondary' : 'rgba(0,0,0,0.35)',
                                textAlign: 'center',
                                lineHeight: 1.3,
                                maxWidth: 130,
                            }}>
                                Win more work
                            </Typography>
                        </Box>
                    </Box>

                    {/* Tab content */}
                    {productCards.map((product, i) => activeTab === i && (
                        <Grid key={product.name} container spacing={3}
                            sx={{
                                alignItems: "center",
                                mt: 0,
                                height: '515px',
                                // border: BORDER_BLACK,
                            }}>
                            {/* Left: product info */}
                            <Grid item xs={12} md={5}>
                                <Box sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 1.5,
                                    backgroundColor: `${product.color}12`,
                                    border: `1px solid ${product.color}70`,
                                    borderRadius: 2, px: 2, py: 1, mb: 3
                                }}>
                                    <Box sx={{
                                        width: 32, height: 32, borderRadius: 1,
                                        backgroundColor: `${product.color}20`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <product.Icon sx={{ color: product.color, fontSize: 18 }} />
                                    </Box>
                                    <Typography variant="caption" sx={{ color: product.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                        {product.tagline}
                                    </Typography>
                                </Box>
                                <Typography variant="h3" sx={{ mb: 2 }}>{product.name}</Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                                    {product.description}
                                </Typography>
                                <Stack spacing={1.25} sx={{ mb: 4 }}>
                                    {product.features.map(f => (
                                        <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                            <CheckCircleIcon sx={{ fontSize: CHECKCIRCLE_SIZE, color: product.color, flexShrink: 0 }} />
                                            <Typography variant="body2">{f}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                                <Stack direction="row" spacing={2}>
                                    <FancyGoldButton to={product.path} color={product.color} endIcon={<ArrowForwardIcon />}>
                                        Learn more
                                    </FancyGoldButton>
                                    <FancyLightButton to={`/pricing?tab=${product.pricingTab}`} color={product.color}>
                                        See pricing
                                    </FancyLightButton>
                                </Stack>
                            </Grid>

                            {/* Right: video thumbnail */}
                            <Grid item xs={12} md={7}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                                    <Chip
                                        icon={<PlayCircleIcon sx={{ fontSize: '16px !important', color: `${product.color} !important` }} />}
                                        label="Quick Tour"
                                        size="medium"
                                        sx={{

                                            backgroundColor: `${product.color}12`,
                                            border: `1px solid ${product.color}70`,
                                            color: product.color,

                                            fontWeight: 600,
                                            fontSize: '12px',
                                            letterSpacing: '0.3px',
                                        }}
                                    />
                                </Box>
                                <Box
                                    onClick={() => { setVideoId(product.videoid); setVideoAspect(product.videoAspect ?? '56.25%'); setVideoOpen(true) }}
                                    sx={{
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        boxShadow: `0 8px 40px ${product.color}20`,
                                        border: `1px solid ${product.color}20`,
                                        position: 'relative',
                                        paddingTop: product.videoAspect ?? '56.25%',
                                        cursor: 'pointer',
                                        '&:hover .play-overlay': { backgroundColor: 'rgba(0,0,0,0.55)' },
                                        '&:hover .play-icon': { transform: 'scale(1.1)' },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={product.thumb ?? `https://img.youtube.com/vi/${product.videoid}/maxresdefault.jpg`}
                                        alt={`${product.name} demo`}
                                        sx={{
                                            position: 'absolute',
                                            top: 0, left: 0,
                                            width: '100%', height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                    <Box
                                        className="play-overlay"
                                        sx={{
                                            position: 'absolute',
                                            top: 0, left: 0,
                                            width: '100%', height: '100%',
                                            backgroundColor: 'rgba(0,0,0,0.35)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'background-color 0.2s',
                                        }}
                                    >
                                        <PlayCircleIcon
                                            className="play-icon"
                                            sx={{
                                                fontSize: 72,
                                                color: 'white',
                                                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))',
                                                transition: 'transform 0.2s',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    ))}

                    {/* Win tab content  testimonials carousel */}
                    {activeTab === 3 && (
                        <Grid container spacing={3}
                            sx={{
                                alignItems: "start",
                                height: { md: '515px' },
                                // border: BORDER_BLACK,
                                mt: 0
                            }}>
                            {/* Left: header + navigation */}
                            <Grid item xs={12} md={5}>
                                <Box sx={{
                                    display: 'inline-flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    backgroundColor: 'rgba(70,145,39,0.08)',
                                    border: '1px solid rgba(70,145,39,0.3)',
                                    borderRadius: 2, px: 2, py: 1, mb: 3
                                }}>
                                    <WorkspacePremiumIcon sx={{ color: COLOR_TERTIARY, fontSize: 18 }} />
                                    <Typography variant="caption" sx={{ color: COLOR_TERTIARY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                        Customer Stories
                                    </Typography>
                                </Box>
                                <Typography variant="h3" sx={{ mb: 2 }}>Trusted by UK & Ireland bid teams</Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                                    See what our customers say about winning more public sector work with Contract Finder Pro.
                                </Typography>
                                <Box sx={{ display: 'flex', mb: 1 }}>
                                    {[...Array(5)].map((_, si) => (
                                        <StarIcon key={si} sx={{ fontSize: 20, color: COLOR_PRIMARY }} />
                                    ))}
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                                    Rated 5 stars by verified buyers
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <IconButton
                                        onClick={() => setCarouselIdx(i => (i - 1 + testimonials.length) % testimonials.length)}
                                        sx={{ border: '1.5px solid rgba(70,145,39,0.4)', color: COLOR_TERTIARY, '&:hover': { backgroundColor: 'rgba(70,145,39,0.08)' } }}
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>
                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 48, textAlign: 'center' }}>
                                        {carouselIdx + 1} / {testimonials.length}
                                    </Typography>
                                    <IconButton
                                        onClick={() => setCarouselIdx(i => (i + 1) % testimonials.length)}
                                        sx={{ border: '1.5px solid rgba(70,145,39,0.4)', color: COLOR_TERTIARY, '&:hover': { backgroundColor: 'rgba(70,145,39,0.08)' } }}
                                    >
                                        <ChevronRightIcon />
                                    </IconButton>
                                    <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
                                        {testimonials.map((_, i) => (
                                            <Box
                                                key={i}
                                                onClick={() => setCarouselIdx(i)}
                                                sx={{
                                                    width: i === carouselIdx ? 20 : 8,
                                                    height: 8,
                                                    borderRadius: 4,
                                                    backgroundColor: i === carouselIdx ? COLOR_TERTIARY : 'rgba(70,145,39,0.25)',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s',
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Stack>
                            </Grid>

                            {/* Right: testimonial card */}
                            <Grid item xs={12} md={7}>
                                <Box sx={{
                                    backgroundColor: 'white',
                                    border: '1px solid rgba(70,145,39,0.2)',
                                    borderRadius: 3,
                                    p: { xs: 3, md: 5 },
                                    boxShadow: '0 8px 40px rgba(70,145,39,0.1)',
                                    minHeight: { md: 320 },
                                    maxHeight: { md: 500 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                }}>
                                    <Box>
                                        <Box sx={{
                                            display: 'flex', mb: 2
                                        }}>
                                            {[...Array(5)].map((_, si) => (
                                                <StarIcon key={si} sx={{ fontSize: 18, color: COLOR_PRIMARY }} />
                                            ))}
                                        </Box>
                                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, fontStyle: 'italic', overflow: 'auto', maxHeight: 300 }}>
                                            "{testimonials[carouselIdx].quote}"
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: '1px solid rgba(70,145,39,0.1)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{
                                                width: 40, height: 40, borderRadius: '50%',
                                                backgroundColor: 'rgba(70,145,39,0.12)',
                                                border: '1.5px solid rgba(70,145,39,0.3)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontWeight: 700, color: COLOR_TERTIARY, fontSize: '14px',
                                                flexShrink: 0,
                                            }}>
                                                {testimonials[carouselIdx].name === 'Anonymous' ? '?' : testimonials[carouselIdx].name.charAt(0)}
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" fontWeight={700}>{testimonials[carouselIdx].name}</Typography>
                                                <Typography variant="caption" sx={{ color: COLOR_TERTIARY }}>✓ Verified Buyer</Typography>
                                            </Box>
                                        </Box>
                                        <Typography variant="caption" color="text.disabled">{testimonials[carouselIdx].timeAgo}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </Container>
            </Box>

            {/* Problem Box */}
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            <ProblemBox quote={homeProblemStatement} items={homeProblems} />
            <WaveDivider bottom={BG_LIGHT} top={DARK_800} inverted />

            <TrustedPartnerSection />
            <StatsSection stats={stats} liveFirstStat={liveTotal} />

            <HowItWorksSection subtitle={homeStepsTitle} steps={homeSteps} />

            <CtaSection onDemoClick={() => setDemoOpen(true)} />
            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
            <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} videoId={videoId} aspectRatio={videoAspect} />
        </Box >
    )
}
